import {
  ComponentProps,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import AuthViewContainer from "modules/auth/layout/AuthViewContainer";
import { AuthTopBlock } from "modules/auth/layout/AuthTopBlock";
import logger from "logging/logger";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { Box } from "@remo-co/ui-core/src/components/Box";
import useAuth from "modules/auth/hooks/useAuth";
import SignInView from "modules/auth/signIn/SignInView";
import SignUpView from "modules/auth/signUp/SignUpView";
import { getEventFromToken } from "services/apiService/apis";
import { CompanyLogo } from "modules/company/components/CompanyLogo";
import EventTopBlock from "modules/auth/info/EventInfo";
import AuthViewContent from "modules/auth/layout/AuthViewContent";
import { useI18n } from "i18n";
import { TRACKING_CONTEXT } from "modules/tracking";
import { IEvent } from "modules/event/types";
import { ICompany } from "modules/company/types";
import { IAuthType } from "modules/auth/types";
import { TrialFeaturesList } from "modules/company";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { useStyles } from "./styles";

interface Props {
  title?: string;
  authType: IAuthType;
  trackEvent?: string;
  showTrialFeatureList?: boolean;
  setEmail: (email: string) => void;
  children:
    | ReactElement<ComponentProps<typeof SignInView>>
    | ReactElement<ComponentProps<typeof SignUpView>>;
}

export const CommonAuthContainer = ({
  title,
  authType,
  trackEvent,
  showTrialFeatureList,
  setEmail,
  children,
}: Props): JSX.Element => {
  const { t } = useI18n(["common", "auth"]);
  const [event, setEvent] = useState<IEvent>();
  const [company, setCompany] = useState<ICompany>();
  const { isLoading } = useAuth();
  const { track } = useContext(TRACKING_CONTEXT);
  const classes = useStyles();
  const { redirectTo, email } = useQueryParams();

  useEffect(() => {
    const redirectUrl = redirectTo ? `${redirectTo}` : "";

    if (email) {
      setEmail(email.toString());
    }

    if (!redirectUrl) return;

    if (redirectUrl.startsWith("/e/") || redirectUrl.startsWith("/s/")) {
      const token = redirectUrl.substring(3);

      // eslint-disable-next-line promise/catch-or-return, promise/prefer-await-to-then
      getEventFromToken({ inviteToken: token }).then((res) => {
        // eslint-disable-next-line promise/always-return
        if (res.isSuccess) {
          setEvent(res.event);
          setCompany(res.company as ICompany);
        } else {
          logger.error(
            `[CommonAuthContainer][${authType}] error occured while extracting event from token`,
            {
              response: res.error,
            },
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoading && trackEvent) {
      track(trackEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <>
      <Box display="flex">
        <AuthViewContainer
          className={showTrialFeatureList ? classes.justifyContent : undefined}
          showTrialFeatureList={showTrialFeatureList}
        >
          {company && company.logoURL && (
            <Box className={classes.companyLogoContainer}>
              <CompanyLogo url={company.logoURL} />
            </Box>
          )}
          <Box display="flex" justifyContent="center" width="100%">
            <AuthViewContent>
              {!event && (
                <AuthTopBlock
                  title={title}
                  subtitle={t("auth:formerly.remo")}
                />
              )}
              {event && <EventTopBlock event={event} company={company} />}
              {children}
            </AuthViewContent>
          </Box>
          {showTrialFeatureList && (
            <Box className={classes.trialFeaturesContainer}>
              <TrialFeaturesList />
            </Box>
          )}
        </AuthViewContainer>
      </Box>
      {isLoading && (
        <div className="login-loading">
          <PageLoader message={t("wait")} />
        </div>
      )}
    </>
  );
};
